import TaskList from "./components/TaskList";
import React, { useState, useEffect, Fragment, useCallback, useRef, useMemo } from "react";
import "./App.css";
import YouTubePlayer from "./components/YouTubePlayer";
import TaskPlayerBar from "./components/TaskPlayerBar";
import BackgroundPalette from "./components/BackgroundPalette";
import LandingPage from "./components/LandingPage";
import CompletedTasksView from "./components/CompletedTasksView";
import CategoryModal from "./components/CategoryModal";
import DueDateSelector from "./components/DueDateSelector";
import Toast from "./components/Toast";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import UserProfileButton from "./components/UserProfileButton";
import PasswordResetModal from "./components/PasswordResetModal";
import { usePostHog } from 'posthog-js/react';
import {
  MusicalNoteIcon,
  XMarkIcon,
  QueueListIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline";
import { 
  MoonIcon,
  SunIcon,
} from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";
import { taskService, settingsService, cleanupDeprecatedCategories } from "./services/database";
import { Task } from "./types";
import { TIMER_PRESETS, PresetType } from "./constants/timerPresets";
import { FEATURES } from "./config/features";

// Sound file paths for timer functionality
export const SOUND_PATHS = {
  workEnd: "/sounds/work-break-end.mp3",
  breakEnd: "/sounds/work-break-end.mp3",
  twoMinWarning: "/sounds/two-min-warning.mp3",
  countdown: "/sounds/work-break-end.mp3",
};

const timeValue = (value?: string | null): number => {
  if (!value) {
    return 0;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

// Timer state types
type TimerState = "ready" | "running" | "paused" | "break";

function App() {
  const { isPasswordRecovery } = useAuth();
  const posthog = usePostHog();
  
  const [tasks, setTasks] = useState<Task[]>([]);
  const [activeTaskId, setActiveTaskId] = useState<number | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [currentSessionType, setCurrentSessionType] = useState<
    "work" | "shortBreak"
  >("work");
  const [showYouTubePlayer, setShowYouTubePlayer] = useState(false);
  const [isYouTubeImmersive, setIsYouTubeImmersive] = useState(false);
  const [isAppMinimized, setIsAppMinimized] = useState(false);
  // Mobile viewport detection (used to prevent compact TaskPlayerBar on < 640px)
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 640;
  });
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [showBackgroundPalette, setShowBackgroundPalette] = useState(false);

  const [darkMode, setDarkMode] = useState(true);
  const [soundSettings, setSoundSettings] = useState<{
    countdownBeeps: boolean;
    twoMinWarning: boolean;
  }>({
    countdownBeeps: true,
    twoMinWarning: true,
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showLandingPage, setShowLandingPage] = useState(false);
  const [landingPageShouldExit, setLandingPageShouldExit] = useState(false);
  const [pendingAction, setPendingAction] = useState<'drawer' | 'music' | null>(null);
  const [paletteAnchorPosition, setPaletteAnchorPosition] = useState({ top: 80, right: 16 });
  
  // Fixed add task box state
  const [fixedBoxTask, setFixedBoxTask] = useState("");
  const [fixedBoxCategory, setFixedBoxCategory] = useState<string | undefined>(undefined);
  const [fixedBoxDueDate, setFixedBoxDueDate] = useState<Date | null>(null);
  const [showFixedBox, setShowFixedBox] = useState(false);
  const fixedBoxInputRef = useRef<HTMLTextAreaElement>(null);
  
  // Password reset modal state
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);

  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // Track viewport width to keep behavior consistent when resizing
  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth < 640);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // On mobile (< 640px), always render TaskPlayerBar in expanded mode
  const shouldMinimizeTaskBar = isAppMinimized && !isMobileViewport;
  
  // Timer preset state - shared with TaskPlayerBar
  const [selectedPreset, setSelectedPreset] = useState<PresetType>("classic");
  
  // Ref for background button to calculate palette position
  const backgroundButtonRef = useRef<HTMLButtonElement>(null);

  // Show password reset modal when PASSWORD_RECOVERY event is detected
  useEffect(() => {
    if (isPasswordRecovery) {
      setShowPasswordResetModal(true);
    }
  }, [isPasswordRecovery]);

  const autoGrowFixed = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    if (!showFixedBox) return;
    const raf = requestAnimationFrame(() => {
      if (fixedBoxInputRef.current) {
        fixedBoxInputRef.current.focus();
        autoGrowFixed(fixedBoxInputRef.current);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [showFixedBox]);

  useEffect(() => {
    autoGrowFixed(fixedBoxInputRef.current);
  }, [fixedBoxTask]);
  
  // Ref for scrollable area to enable auto-scroll
  const scrollableAreaRef = useRef<HTMLDivElement>(null);
  const [activeTasksTab, setActiveTasksTab] = useState<"tasks" | "completed">("tasks");
  
  // Core timer state
  const [currentTime, setCurrentTime] = useState(0);
  const [totalTime, setTotalTime] = useState(25 * 60); // Default to 25 minutes
  const [currentSession, setCurrentSession] = useState(1); // Current work session (1-based)
  const [timerState, setTimerState] = useState<TimerState>("ready");
  const [completedSessions, setCompletedSessions] = useState(0);
  const [totalCompletedPomodoros, setTotalCompletedPomodoros] = useState(0);
  const [timerStartTime, setTimerStartTime] = useState<number | null>(null);
  const [startTimeLeft, setStartTimeLeft] = useState<number | null>(null);
  const [autoSavedTime, setAutoSavedTime] = useState(0); // Track time already auto-saved
  const [pausedAtTime, setPausedAtTime] = useState<number | null>(null); // Where timer was paused
  const [timerStartingPosition, setTimerStartingPosition] = useState<number>(0); // What time position the current timer session started from

  // Control fixed box visibility based on Upcoming tasks
  useEffect(() => {
    const upcomingTaskCount = tasks.filter(t => !t.isToday && !t.completed).length;
    setShowFixedBox(upcomingTaskCount > 0);
  }, [tasks]);


  // Function to refresh tasks from database
  const refreshTasks = useCallback(async () => {
    try {
      const loadedTasks = await taskService.getAllTasks();
      setTasks(loadedTasks);
      return loadedTasks;
    } catch (error) {
      console.error("Failed to refresh tasks:", error);
      return [];
    }
  }, []);

  // Load tasks and settings from IndexedDB on initial render
  useEffect(() => {
    const loadData = async () => {
      try {
        // Clean up deprecated categories first
        await cleanupDeprecatedCategories();
        
        // Hydrate category colors before anything renders tasks or category badges
        await taskService.getAllCategoryRecords();

        // Load tasks
        const loadedTasks = await refreshTasks();

        // Load active task ID
        const activeId = await settingsService.getSetting("activeTaskId", null);
        if (activeId !== null) {
          // Verify the task still exists
          const taskExists = loadedTasks.some((task) => task.id === activeId);
          if (taskExists) {
            setActiveTaskId(activeId);
          } else {
            // If the task doesn't exist anymore, clear the active task ID
            settingsService.setSetting("activeTaskId", null);
          }
        }

        // Load dark mode setting (default to dark for first launch)
        try {
          const savedDarkMode = await settingsService.getSetting("darkMode", true);
          setDarkMode(savedDarkMode ?? true); // Ensure it's never null/undefined
        } catch (error) {
          console.error('Failed to load dark mode, using default:', error);
          setDarkMode(true);
        }

        // Load sound settings
        try {
          const savedSoundSettings = await settingsService.getSetting(
            "soundSettings",
            {
              countdownBeeps: true,
              twoMinWarning: true,
            }
          );
          setSoundSettings(savedSoundSettings);
        } catch (error) {
          console.error('Failed to load sound settings:', error);
          setSoundSettings({ countdownBeeps: true, twoMinWarning: true });
        }

        // Load timer preset
        try {
          const savedPreset = await settingsService.getSetting("selectedPreset", "classic");
          // Validate that the preset exists
          if (Object.keys(TIMER_PRESETS).includes(savedPreset)) {
            setSelectedPreset(savedPreset as PresetType);
          }
        } catch (error) {
          console.error('Failed to load timer preset:', error);
          setSelectedPreset("classic");
        }

        // Load background image - default to Valley for first-time users
        try {
          const savedBackgroundImage = await settingsService.getSetting("backgroundImage", null);
          
          if (savedBackgroundImage === null || savedBackgroundImage === undefined) {
            // First-time user: set Valley as default
            const defaultBackground = '/backgrounds/nature/nature-valley.jpg';
            setBackgroundImage(defaultBackground);
            await settingsService.setSetting("backgroundImage", defaultBackground);
          } else {
            setBackgroundImage(savedBackgroundImage);
          }
        } catch (error) {
          console.error('Failed to load background image, using default:', error);
          const defaultBackground = '/backgrounds/nature/nature-valley.jpg';
          setBackgroundImage(defaultBackground);
          // Don't try to save if there was an error
        }

        // Load app minimized state
        try {
          const savedIsAppMinimized = await settingsService.getSetting("isAppMinimized", false);
          setIsAppMinimized(savedIsAppMinimized);
        } catch (error) {
          console.error('Failed to load app minimized state:', error);
          setIsAppMinimized(false);
        }

        // ✅ MOVED: Run recovery logic AFTER tasks are loaded
        try {
          // Primary recovery method: check for comprehensive recovery data
          const recoveryDataStr = localStorage.getItem('timerRecoveryData');
          if (recoveryDataStr) {
            const recoveryData = JSON.parse(recoveryDataStr);
            const { taskId, elapsedTime, timestamp } = recoveryData;
            
            
            // Validate recovery data (must be from recent session, max 1 minute old)
            const timeSinceRecovery = Date.now() - timestamp;
            if (timeSinceRecovery < 60000 && taskId && elapsedTime > 0) {
              
              
              // Check if this task still exists in the loaded tasks
              const existingTask = loadedTasks.find(t => t.id === taskId);
              if (existingTask) {
                // Update directly using loadedTasks to avoid stale state in updateTaskTime
                const newTimeSpent = (existingTask.timeSpent || 0) + elapsedTime;
                await taskService.updateTask(taskId, {
                  ...existingTask,
                  timeSpent: newTimeSpent,
                });
                // Update local state immediately so UI reflects recovered time
                setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, timeSpent: newTimeSpent } : t));
                
              } else {
                console.warn("Recovered task no longer exists, time not restored");
              }
            } else {
              
            }
            
            // Clean up recovery data
            localStorage.removeItem('timerRecoveryData');
          } else {
            
          }
          
          // Clean up all timer-related localStorage data
          localStorage.removeItem('timerBeaconSent');
          localStorage.removeItem('timerStartTime');
          localStorage.removeItem('timerPauseTime');
          localStorage.removeItem('timerStartTimeHighPrecision');
          localStorage.removeItem('timerTimeOffset');
          localStorage.removeItem('currentTimeAtStart');
          
        } catch (error) {
          console.error("Error during timer recovery after page refresh:", error);
          // Clean up potentially corrupted data
          localStorage.removeItem('timerRecoveryData');
          localStorage.removeItem('timerBeaconSent');
          localStorage.removeItem('timerStartTime');
          localStorage.removeItem('timerPauseTime');
        }

        // Mark initialization as complete
        setIsInitialized(true);
      } catch (error) {
        console.error("Failed to load data:", error);
        setIsInitialized(true); // Still mark as initialized even if there's an error
      }
    };

    loadData();
  }, [refreshTasks]);

  // Check if user has seen the landing page
  useEffect(() => {
    if (isInitialized) {
      const hasSeenLanding = localStorage.getItem('hasSeenLanding');
      if (!hasSeenLanding) {
        setShowLandingPage(true);
      }
    }
  }, [isInitialized]);

  // Handler for "Start Planning"
  const handleStartPlanning = () => {
    localStorage.setItem('hasSeenLanding', 'true');
    setShowLandingPage(false);
    setIsDrawerOpen(true); // Open task drawer
  };

  // Handler for landing page exit completion
  const handleLandingExitComplete = () => {
    localStorage.setItem('hasSeenLanding', 'true');
    setShowLandingPage(false);
    setLandingPageShouldExit(false);
    
    // Execute pending action
    if (pendingAction === 'drawer') {
      setIsDrawerOpen(true);
    } else if (pendingAction === 'music') {
      setShowYouTubePlayer(true);
    }
    setPendingAction(null);
  };

  // Wrapper for opening task drawer
  const handleToggleDrawer = () => {
    if (showLandingPage) {
      setLandingPageShouldExit(true);
      setPendingAction('drawer');
    } else {
      setIsDrawerOpen(!isDrawerOpen);
    }
  };

  // Wrapper for opening music player
  const handleToggleMusicPlayer = () => {
    if (showLandingPage && !showYouTubePlayer) {
      setLandingPageShouldExit(true);
      setPendingAction('music');
    } else {
      const newState = !showYouTubePlayer;
      setShowYouTubePlayer(newState);
      
      // Track music player opened
      if (newState) {
        posthog?.capture('music_player_opened');
      }
    }
  };

  // Apply dark mode class to body
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Save dark mode preference
  useEffect(() => {
    if (isInitialized) {
      settingsService.setSetting("darkMode", darkMode).catch((err) => {
        console.error("Failed to save dark mode setting:", err);
      });
    }
  }, [darkMode, isInitialized]);

  // Save timer preset preference
  useEffect(() => {
    if (isInitialized) {
      settingsService.setSetting("selectedPreset", selectedPreset).catch((err) => {
        console.error("Failed to save preset setting:", err);
      });
    }
  }, [selectedPreset, isInitialized]);

  // Save app minimized state
  useEffect(() => {
    if (isInitialized) {
      settingsService.setSetting("isAppMinimized", isAppMinimized).catch((err) => {
        console.error("Failed to save app minimized state:", err);
      });
    }
  }, [isAppMinimized, isInitialized]);

  // Save background image preference
  useEffect(() => {
    if (isInitialized) {
      settingsService.setSetting("backgroundImage", backgroundImage).catch((err) => {
        console.error("Failed to save background image setting:", err);
      });
    }
  }, [backgroundImage, isInitialized]);

  // Update timer durations when preset changes (including on load)
  useEffect(() => {
    if (isInitialized) {
      // Only update if timer is in ready state (not running, paused, or in break)
      // AND if currentTime is already 0 (to avoid resetting a paused timer)
      if (timerState === "ready" && currentSessionType === "work" && currentTime === 0) {
        setTotalTime(TIMER_PRESETS[selectedPreset].workDuration);
        setCurrentTime(0);
        setTimerStartingPosition(0);
      }
    }
  }, [selectedPreset, isInitialized, timerState, currentSessionType, currentTime]);

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    
    // Track dark mode toggle
    posthog?.capture('dark_mode_toggled', {
      enabled: newMode
    });
  };

  const toggleBackgroundPalette = () => {
    if (!showBackgroundPalette && backgroundButtonRef.current) {
      // Calculate position when opening the palette
      const buttonRect = backgroundButtonRef.current.getBoundingClientRect();
      const paletteWidth = 288; // w-72 = 18rem = 288px
      const buttonCenter = buttonRect.left + buttonRect.width / 2;
      
      // Center the palette relative to the button and position below the header
      setPaletteAnchorPosition({
        top: buttonRect.bottom + 12, // 12px gap below button for clear separation from header
        right: window.innerWidth - buttonCenter - paletteWidth / 2, // Center-align with button
      });
    }
    setShowBackgroundPalette(!showBackgroundPalette);
  };

  const handleBackgroundSelect = (backgroundPath: string | null) => {
    setBackgroundImage(backgroundPath);
  };

  const updateTaskTime = useCallback(async (taskId: number, additionalTime: number) => {
    try {
      const task = await taskService.getTask(taskId);
      if (!task) {
        console.error("Task not found:", taskId);
        return;
      }

      const newTimeSpent = (task.timeSpent || 0) + Math.max(0, additionalTime);

      await taskService.updateTask(taskId, {
        timeSpent: newTimeSpent,
      });

      setTasks((prevTasks) =>
        prevTasks.map((t) =>
          t.id === taskId
            ? {
                ...t,
                timeSpent: newTimeSpent,
              }
            : t
        )
      );
    } catch (error) {
      console.error("Failed to update task time:", error);
    }
  }, []);


  // Add beforeunload event listener to warn users when refreshing with active timer
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isTimerRunning) {
        // Get the active task
        const task = tasks.find((task) => task.id === activeTaskId);
        
        // If there's an active task, immediately save elapsed time to IndexedDB
        if (task && timerStartTime && currentSessionType === "work") {
          try {
            // Calculate total elapsed time since timer started
            const totalElapsed = Math.floor((Date.now() - timerStartTime) / 1000);
            
            // Only save the time that hasn't been auto-saved yet (avoid double-counting)
            const timeToSave = totalElapsed - autoSavedTime;
            
            if (timeToSave > 0) {
              // Store recovery data in localStorage for post-refresh restoration
              localStorage.setItem('timerRecoveryData', JSON.stringify({
                taskId: activeTaskId,
                elapsedTime: timeToSave, // Store only unsaved time
                currentTime: currentTime,
                totalTime: totalTime,
                sessionType: currentSessionType,
                timestamp: Date.now()
              }));
              
              
              // ❌ REMOVED: Don't immediately save to IndexedDB on beforeunload!
              // The user might cancel the refresh. Let pagehide handle actual page leave.
            } else {
              // All time already auto-saved, just store minimal recovery data
              localStorage.setItem('timerRecoveryData', JSON.stringify({
                taskId: activeTaskId,
                elapsedTime: 0, // No additional time to save
                currentTime: currentTime,
                totalTime: totalTime,
                sessionType: currentSessionType,
                timestamp: Date.now()
              }));
              
            }
              
            // For browsers that support sendBeacon, store additional recovery data
            if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
              const saveData = JSON.stringify({
                taskId: activeTaskId,
                elapsedTime: timeToSave > 0 ? timeToSave : 0,
                timestamp: Date.now()
              });
              
              // Store beacon data as additional recovery mechanism
              try {
                localStorage.setItem('timerBeaconSent', saveData);
              } catch (beaconError) {
                console.error("Error storing beacon data:", beaconError);
              }
            }
            
            // Important: do NOT schedule cleanup here; it can fire while the
            // confirmation dialog is open and wipe recovery data before reload.
          } catch (error) {
            console.error("Error saving timer data during page refresh:", error);
          }
        }
        
        // DON'T pause the timer here - only show the confirmation dialog
        // Timer will continue running if user cancels the refresh
        
        // Standard way to show a confirmation dialog
        e.preventDefault();
        e.returnValue = "You have an active timer running. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    const handlePageHide = () => {
      // This fires when the page is actually leaving (user clicked OK)
      // ✅ SIMPLIFIED: Don't try async operations during page unload - they're unreliable
      // Recovery logic will handle saving the time after page reload
      
    };

    // Handle visibility change (tab switching, minimizing, etc.)
    const handleVisibilityChange = () => {
      // Original logic to pause timer when hidden is removed here.
      // We no longer want to pause the timer when the tab is not visible.
      // If needed in the future, logic for when the tab becomes *visible* could be added here.
      // Example: Check elapsed time and adjust timeLeft if drift occurred due to throttling.
      /* 
      if (document.visibilityState === 'hidden' && isTimerRunning) {
        // Get the active task
        const task = tasks.find((task) => task.id === activeTaskId);
        
        // If there's an active task, store the current time
        if (task) {
          try {
            // Store the current time for accurate calculation when visibility changes
            localStorage.setItem('timerPauseTime', Date.now().toString());
          } catch (error) {
            console.error("Error storing pause time during visibility change:", error);
          }
        }
        
        // User is navigating away or refreshing, pause the timer
        setIsTimerRunning(false);
      }
      */
    };

    
    // ✅ REMOVED: Recovery now handled after tasks are loaded in initialization useEffect

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    isTimerRunning,
    activeTaskId,
    tasks,
    updateTaskTime,
    timerStartTime,
    autoSavedTime,
    currentSessionType,
    currentTime,
    totalTime,
  ]);

  // Save active task ID when it changes
  useEffect(() => {
    // Only save if we've completed initialization
    if (!isInitialized) {
      return;
    }

    settingsService.setSetting("activeTaskId", activeTaskId).catch((err) => {
      console.error("Failed to save active task ID:", err);
    });
  }, [activeTaskId, isInitialized]);

  // Save sound settings to IndexedDB
  useEffect(() => {
    // Only save if we've completed initialization
    if (!isInitialized) {
      return;
    }

    settingsService.setSetting("soundSettings", soundSettings).catch((err) => {
      console.error("Failed to save sound settings:", err);
    });
  }, [soundSettings, isInitialized]);

  const toggleSound = (setting: keyof typeof soundSettings) => {
    setSoundSettings((prev: typeof soundSettings) => ({
      ...prev,
      [setting]: !prev[setting],
    }));
  };

  // Add a new task
  const addTask = async (title: string, category?: string, dueDate?: Date | null) => {
    try {
      // Create new task
      const currentDate = new Date().toISOString();
      const newTask: Omit<Task, "id"> = {
        text: title,
        completed: false,
        timeSpent: 0,
        createdAt: currentDate,
        category,
        isToday: false, // Initialize as not today
        dueDate,
        todayOrder: null,
        upcomingOrder: 0,
        completedAt: null,
      };

      const taskId = await taskService.addTask(newTask);
      
      // Update tasks state
      const createdTask = { ...newTask, id: taskId };
      
      // Instead of appending and re-sorting, simply insert at the beginning for uncompleted tasks
      setTasks((prevTasks) => {
        const updatedExisting = prevTasks.map((task) => {
          if (!task.isToday && !task.completed) {
            return {
              ...task,
              upcomingOrder: (task.upcomingOrder ?? 0) + 1,
            };
          }
          return task;
        });
        return [createdTask, ...updatedExisting];
      });

      return taskId;
    } catch (error) {
      console.error("Failed to add task:", error);
      throw error;
    }
  };

  // Update a task
  const updateTask = async (taskId: number, text: string, category?: string) => {
    try {
      // Find the task
      const taskIndex = tasks.findIndex((t) => t.id === taskId);
      if (taskIndex === -1) return;

      const task = tasks[taskIndex];
      
      // Update task with new text and category
      const updatedTask = { 
        ...task, 
        text,
        category // Always update category (can be undefined to clear it)
      };
      
      // Update in database
      await taskService.updateTask(taskId, updatedTask);
      
      // Update state
      setTasks((prevTasks) => {
        const newTasks = [...prevTasks];
        newTasks[taskIndex] = updatedTask;
        return newTasks;
      });
    } catch (error) {
      console.error("Failed to update task:", error);
    }
  };

  // Delete a task
  const deleteTask = async (id: number) => {
    try {
      await taskService.deleteTask(id);
      setTasks((prevTasks) => prevTasks.filter((task) => task.id !== id));

      if (activeTaskId === id) {
        setActiveTaskId(null);
      }
    } catch (error) {
      console.error("Failed to delete task:", error);
    }
  };

  const handleRestoreTask = async (taskId: number) => {
    try {
      await taskService.restoreTask(taskId);
      await refreshTasks();
    } catch (error) {
      console.error("Failed to restore task:", error);
    }
  };

  const resetTimerStateAfterTaskChange = useCallback(() => {
    setIsTimerRunning(false);
    setTimerState("ready");
    setTimerStartTime(null);
    setStartTimeLeft(null);
    setCurrentTime(0);
    setTimerStartingPosition(0);
    setPausedAtTime(null);
    setAutoSavedTime(0);

    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem("timerStartTime");
        localStorage.removeItem("currentTimeAtStart");
        localStorage.removeItem("timerRecoveryData");
        localStorage.removeItem("timerBeaconSent");
        localStorage.removeItem("timerPauseTime");
        localStorage.removeItem("timerStartTimeHighPrecision");
        localStorage.removeItem("timerTimeOffset");
      } catch (storageError) {
        console.error("Failed to clear timer persistence data:", storageError);
      }
    }
  }, []);

  const toggleTaskCompletion = async (taskId: number) => {
    try {
      const wasActiveTask = activeTaskId === taskId;
      const todayTasksBeforeToggle = tasks
        .filter((task) => task.isToday && !task.completed)
        .sort((a, b) => (a.todayOrder ?? 0) - (b.todayOrder ?? 0));
      const previousTodayIndex = todayTasksBeforeToggle.findIndex(
        (task) => task.id === taskId
      );
      const previousTodayOrder =
        previousTodayIndex !== -1
          ? (todayTasksBeforeToggle[previousTodayIndex].todayOrder ?? 0)
          : null;
      const nextTaskIdBeforeToggle =
        previousTodayIndex !== -1 &&
        previousTodayIndex + 1 < todayTasksBeforeToggle.length
          ? todayTasksBeforeToggle[previousTodayIndex + 1].id
          : null;

      if (
        wasActiveTask &&
        isTimerRunning &&
        timerStartTime &&
        currentSessionType === "work"
      ) {
        const totalElapsed = Math.floor((Date.now() - timerStartTime) / 1000);
        const timeToSave = totalElapsed - autoSavedTime;

        if (timeToSave > 0) {
          await updateTaskTime(taskId, timeToSave);
        }
      }

      await taskService.toggleTask(taskId);
      const updatedTasks = await refreshTasks();

      if (wasActiveTask) {
        const toggledTask = updatedTasks.find((task) => task.id === taskId);

        if (toggledTask?.completed) {
          const sortedTodayTasks = updatedTasks
            .filter((task) => task.isToday && !task.completed)
            .sort((a, b) => (a.todayOrder ?? 0) - (b.todayOrder ?? 0));

          let nextActiveTaskId: number | null = null;

          if (nextTaskIdBeforeToggle !== null) {
            const preservedNeighbor = sortedTodayTasks.find(
              (task) => task.id === nextTaskIdBeforeToggle
            );
            if (preservedNeighbor) {
              nextActiveTaskId = preservedNeighbor.id;
            }
          }

          if (nextActiveTaskId === null && previousTodayOrder !== null) {
            const nextInSequence = sortedTodayTasks.find(
              (task) => (task.todayOrder ?? 0) > previousTodayOrder
            );

            if (nextInSequence) {
              nextActiveTaskId = nextInSequence.id;
            } else if (sortedTodayTasks.length > 0) {
              // No later task existed; fall back to the first remaining Today task
              nextActiveTaskId = sortedTodayTasks[0].id;
            }
          } else if (nextActiveTaskId === null && sortedTodayTasks.length > 0) {
            nextActiveTaskId = sortedTodayTasks[0].id;
          }

          resetTimerStateAfterTaskChange();

          if (nextActiveTaskId !== null) {
            setActiveTaskId(nextActiveTaskId);
          } else {
            setActiveTaskId(null);
          }
        }
      }
    } catch (error) {
      console.error("Failed to toggle task:", error);
    }
  };

  const handleTasksReorder = async (newTasks: Task[]) => {
    try {
      let todayIndex = 0;
      let upcomingIndex = 0;

      const updatedTasks = newTasks.map((task) => {
        const updatedTask = { ...task };

        // Completed tasks should not participate in ordering
        if (task.completed) {
          updatedTask.todayOrder = null;
          updatedTask.upcomingOrder = null;
          return updatedTask;
        }

        if (task.isToday) {
          updatedTask.todayOrder = todayIndex++;
          updatedTask.upcomingOrder = null;
        } else {
          updatedTask.upcomingOrder = upcomingIndex++;
          updatedTask.todayOrder = null;
        }
        return updatedTask;
      });

      setTasks(updatedTasks);

      for (const task of updatedTasks) {
        await taskService.updateTask(task.id, {
          isToday: task.isToday,
          todayOrder: task.todayOrder ?? null,
          upcomingOrder: task.upcomingOrder ?? null,
        });
      }
    } catch (error) {
      console.error("Failed to reorder tasks:", error);
    }
  };

  // New function to handle setting the 'isToday' status
  const handleSetTaskTodayStatus = async (taskId: number, isToday: boolean) => {
    try {
      await taskService.setTaskTodayStatus(taskId, isToday);
      // Refresh tasks to reflect the change in the UI lists
      return await refreshTasks(); 
    } catch (error) {
      console.error("Failed to set task today status:", error);
      // Optionally re-fetch tasks to revert optimistic UI update if needed
      throw error;
    }
  };

  // Handle fixed box task submission
  const handleFixedBoxSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (fixedBoxTask.trim()) {
      try {
        await addTask(fixedBoxTask.trim(), fixedBoxCategory, fixedBoxDueDate);
        setFixedBoxTask("");
        setFixedBoxCategory(undefined);
        setFixedBoxDueDate(null);
        
        // Scroll to top of the scrollable area to show the newly added task
        if (scrollableAreaRef.current) {
          scrollableAreaRef.current.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      } catch (error) {
        console.error("Failed to add task from fixed box:", error);
      }
    }
  };

  // TaskPlayerBar handlers
  const handlePlayPause = async () => {
    if (isTimerRunning) {
      // Pause timer
      await pauseTimer();
    } else {
      // Start timer
      startTimer();
    }
  };

  const startTimer = () => {
    const startTime = Date.now();
    
    // Use paused position if available, otherwise current time  
    const startingTime = pausedAtTime !== null ? pausedAtTime : currentTime;
    
    // Set all state together to avoid race conditions
    setTimerStartTime(startTime);
    setStartTimeLeft(totalTime - startingTime); // Remaining time when starting
    setTimerStartingPosition(startingTime); // Store starting position in React state
    setTimerState("running");
    setIsTimerRunning(true);
    setAutoSavedTime(0); // Reset auto-save tracking (each start/resume is a fresh session)
    
    // Store start time in localStorage for page refresh persistence
    localStorage.setItem('timerStartTime', startTime.toString());
    localStorage.setItem('currentTimeAtStart', startingTime.toString());
    
    // Clear paused state since we're starting
    setPausedAtTime(null);
  };

  const pauseTimer = async () => {
    // Calculate current position for saving using React state instead of localStorage
    let currentPosition = currentTime;
    if (timerStartTime) {
      const totalElapsed = Math.floor((Date.now() - timerStartTime) / 1000);
      // Use React state instead of localStorage to avoid race conditions
      currentPosition = Math.min(totalTime, timerStartingPosition + totalElapsed);
      
    }
    
    // Save elapsed time to current task before pausing
    if (activeTaskId && timerStartTime && currentSessionType === "work") {
      try {
        const totalElapsed = Math.floor((Date.now() - timerStartTime) / 1000);
        const timeToSave = totalElapsed - autoSavedTime;
        
        if (timeToSave > 0) {
          await updateTaskTime(activeTaskId, timeToSave);
          await refreshTasks();
        }
      } catch (error) {
        console.error("Failed to save task time on pause:", error);
      }
    }

    // Update state - store pause position in React state (can't be cleared by other functions)
    setCurrentTime(currentPosition);
    setPausedAtTime(currentPosition); // Store where we paused for resume
    setTimerState("paused");
    setIsTimerRunning(false);
    setTimerStartTime(null);
    setStartTimeLeft(null);
    setAutoSavedTime(0);
    
    // Clear localStorage items
    localStorage.removeItem('timerStartTime');
    localStorage.removeItem('currentTimeAtStart'); 
    localStorage.removeItem('timerRecoveryData');
    localStorage.removeItem('timerBeaconSent');
  };

  const handleSkipBreak = () => {
    // TODO: Implement skip break functionality
  };

  const handleNextTask = async () => {
    // Save elapsed time to current task before switching
    if (activeTaskId && timerStartTime && currentSessionType === "work") {
      try {
        // Calculate total elapsed time since timer started
        const totalElapsed = Math.floor((Date.now() - timerStartTime) / 1000);
        
        // Only save the time that hasn't been auto-saved yet
        const timeToSave = totalElapsed - autoSavedTime;
        
        if (timeToSave > 0) {
          await updateTaskTime(activeTaskId, timeToSave);
          
          // Refresh tasks to update UI with new time spent
          await refreshTasks();
        }
      } catch (error) {
        console.error("Failed to save task time before switching:", error);
      }
    }

    // Reset timer for new task - always switch to work session
    setIsTimerRunning(false);
    setTimerState("ready");
    setTimerStartTime(null);
    setStartTimeLeft(null);
    setCurrentTime(0);
    setTimerStartingPosition(0);
    setAutoSavedTime(0); // Reset auto-save tracking
    setPausedAtTime(null); // Clear any paused position
    
    // Switch to work session (in case user was on a break)
    setCurrentSessionType("work");
    setTotalTime(TIMER_PRESETS[selectedPreset].workDuration);
    
    // Clear localStorage
    localStorage.removeItem('timerStartTime');
    localStorage.removeItem('currentTimeAtStart');
    localStorage.removeItem('timerRecoveryData'); // Clear recovery data
    localStorage.removeItem('timerBeaconSent'); // Clear beacon data

    const currentIndex = todayTasks.findIndex(t => t.id === activeTaskId);
    if (currentIndex < todayTasks.length - 1) {
      setActiveTaskId(todayTasks[currentIndex + 1].id);
    }
  };

  const handlePreviousTask = async () => {
    // Save elapsed time to current task before switching
    if (activeTaskId && timerStartTime && currentSessionType === "work") {
      try {
        // Calculate total elapsed time since timer started
        const totalElapsed = Math.floor((Date.now() - timerStartTime) / 1000);
        
        // Only save the time that hasn't been auto-saved yet
        const timeToSave = totalElapsed - autoSavedTime;
        
        if (timeToSave > 0) {
          await updateTaskTime(activeTaskId, timeToSave);
          
          // Refresh tasks to update UI with new time spent
          await refreshTasks();
        }
      } catch (error) {
        console.error("Failed to save task time before switching:", error);
      }
    }

    // Reset timer for new task - always switch to work session
    setIsTimerRunning(false);
    setTimerState("ready");
    setTimerStartTime(null);
    setStartTimeLeft(null);
    setCurrentTime(0);
    setTimerStartingPosition(0);
    setAutoSavedTime(0); // Reset auto-save tracking
    setPausedAtTime(null); // Clear any paused position
    
    // Switch to work session (in case user was on a break)
    setCurrentSessionType("work");
    setTotalTime(TIMER_PRESETS[selectedPreset].workDuration);
    
    // Clear localStorage
    localStorage.removeItem('timerStartTime');
    localStorage.removeItem('currentTimeAtStart');
    localStorage.removeItem('timerRecoveryData'); // Clear recovery data
    localStorage.removeItem('timerBeaconSent'); // Clear beacon data

    const currentIndex = todayTasks.findIndex(t => t.id === activeTaskId);
    if (currentIndex > 0) {
      setActiveTaskId(todayTasks[currentIndex - 1].id);
    }
  };

  const handleRestartSession = () => {
    // Stop timer if running
    setIsTimerRunning(false);
    setTimerState("ready");
    setTimerStartTime(null);
    setStartTimeLeft(null);
    setAutoSavedTime(0); // Reset auto-save tracking
    setPausedAtTime(null); // Clear any paused position
    
    // Reset current time to start of session
    setCurrentTime(0);
    setTimerStartingPosition(0);
    
    // Clear localStorage
    localStorage.removeItem('timerStartTime');
    localStorage.removeItem('currentTimeAtStart');
    localStorage.removeItem('timerRecoveryData'); // Clear recovery data
    localStorage.removeItem('timerBeaconSent'); // Clear beacon data
  };

  const handleSkipTask = async () => {
    // Save elapsed time to current task before skipping
    if (activeTaskId && timerStartTime && currentSessionType === "work") {
      try {
        // Calculate total elapsed time since timer started
        const totalElapsed = Math.floor((Date.now() - timerStartTime) / 1000);
        
        // Only save the time that hasn't been auto-saved yet
        const timeToSave = totalElapsed - autoSavedTime;
        
        if (timeToSave > 0) {
          await updateTaskTime(activeTaskId, timeToSave);
          
          // Refresh tasks to update UI with new time spent
          await refreshTasks();
        }
      } catch (error) {
        console.error("Failed to save task time before skipping:", error);
      }
    }

    // Reset timer for new task
    setIsTimerRunning(false);
    setTimerState("ready");
    setTimerStartTime(null);
    setStartTimeLeft(null);
    setCurrentTime(0);
    setTimerStartingPosition(0);
    setAutoSavedTime(0); // Reset auto-save tracking
    setPausedAtTime(null); // Clear any paused position
    
    // Clear localStorage
    localStorage.removeItem('timerStartTime');
    localStorage.removeItem('currentTimeAtStart');
    localStorage.removeItem('timerRecoveryData'); // Clear recovery data
    localStorage.removeItem('timerBeaconSent'); // Clear beacon data

    // Move to next task
    const currentIndex = todayTasks.findIndex(t => t.id === activeTaskId);
    if (currentIndex < todayTasks.length - 1) {
      setActiveTaskId(todayTasks[currentIndex + 1].id);
    }
  };

  // Helper to move an upcoming task to the first position in Today
  const moveUpcomingTaskToTodayFirst = async (taskId: number) => {
    // Get current today tasks and increment their order
    const todayTasks = tasks.filter(t => t.isToday && !t.completed);
    
    // Update all today tasks to shift down by 1
    for (const t of todayTasks) {
      await taskService.updateTask(t.id, { todayOrder: (t.todayOrder ?? 0) + 1 });
    }
    
    // Move the task to Today at position 0
    await taskService.updateTask(taskId, {
      isToday: true,
      todayOrder: 0,
      upcomingOrder: null,
    });
    
    // Re-index remaining upcoming tasks
    const remainingUpcoming = tasks
      .filter(t => !t.isToday && !t.completed && t.id !== taskId)
      .sort((a, b) => (a.upcomingOrder ?? 0) - (b.upcomingOrder ?? 0));
    
    for (let i = 0; i < remainingUpcoming.length; i++) {
      await taskService.updateTask(remainingUpcoming[i].id, { upcomingOrder: i });
    }
    
    await refreshTasks();
  };

  const handleTaskSelect = async (taskId: number | null) => {
    if (taskId === null) return; // Skip null taskId
    // Find the task
    const task = tasks.find(t => t.id === taskId);
    if (!task || task.completed) return; // Skip completed tasks

    // Check if moving from Upcoming to Today would exceed limit
    if (!task.isToday) {
      const todayTaskCount = tasks.filter(t => t.isToday && !t.completed).length;
      if (todayTaskCount >= 5) {
        // Show toast notification
        setToastMessage("Today list is full (5 tasks max)");
        setShowToast(true);
        return; // Block the action
      }
    }

    // Save elapsed time to current task before switching
    if (activeTaskId && timerStartTime && currentSessionType === "work" && activeTaskId !== taskId) {
      try {
        // Calculate total elapsed time since timer started
        const totalElapsed = Math.floor((Date.now() - timerStartTime) / 1000);
        
        // Only save the time that hasn't been auto-saved yet
        const timeToSave = totalElapsed - autoSavedTime;
        
        if (timeToSave > 0) {
          await updateTaskTime(activeTaskId, timeToSave);
          
          // Refresh tasks to update UI with new time spent
          await refreshTasks();
        }
      } catch (error) {
        console.error("Failed to save task time before switching:", error);
      }
    }

    // If task is in Upcoming, move it to first position in Today
    if (!task.isToday) {
      await moveUpcomingTaskToTodayFirst(taskId);
      
      // Track task moved to today
      posthog?.capture('task_moved_to_today', {
        method: 'click_to_select',
        had_category: !!task.category
      });
    }

    // Reset timer for new task if switching to different task
    if (activeTaskId !== taskId) {
      setIsTimerRunning(false);
      setTimerState("ready");
      setTimerStartTime(null);
      setStartTimeLeft(null);
      setCurrentTime(0);
      setTimerStartingPosition(0);
      setAutoSavedTime(0); // Reset auto-save tracking
      
      // Clear localStorage
      localStorage.removeItem('timerStartTime');
      localStorage.removeItem('currentTimeAtStart');
      localStorage.removeItem('timerRecoveryData'); // Clear recovery data
      localStorage.removeItem('timerBeaconSent'); // Clear beacon data
    }

    setActiveTaskId(taskId);
  };

  const handleActiveTaskMovedToUpcoming = useCallback(
    async (movedTaskId: number) => {
      resetTimerStateAfterTaskChange();
      const freshTasks = await taskService.getAllTasks();
      const remainingToday = freshTasks
        .filter((task) => task.isToday && !task.completed && task.id !== movedTaskId)
        .sort((a, b) => (a.todayOrder ?? 0) - (b.todayOrder ?? 0));

      if (remainingToday.length === 0) {
        setActiveTaskId(null);
        await settingsService.setSetting("activeTaskId", null);
        return;
      }

      const nextActiveId = remainingToday[0].id;
      setActiveTaskId(nextActiveId);
      await settingsService.setSetting("activeTaskId", nextActiveId);
    },
    [resetTimerStateAfterTaskChange]
  );

  const handlePresetChange = async (preset: PresetType) => {
    setSelectedPreset(preset);
    
    // Stop timer if running and reset
    if (isTimerRunning) {
      await pauseTimer();
    }
    
    // Reset all session counters and state
    setTotalTime(TIMER_PRESETS[preset].workDuration);
    setCurrentTime(0);
    setTimerStartingPosition(0);
    setPausedAtTime(null); // Clear any paused position
    setCurrentSessionType("work");
    setCurrentSession(1); // Reset session count
    setCompletedSessions(0);
  };

  const handleSkipSession = async () => {
    // Save elapsed time before stopping timer
    if (currentSessionType === "work" && activeTaskId && timerStartTime) {
      try {
        // Calculate total elapsed time since timer started
        const totalElapsed = Math.floor((Date.now() - timerStartTime) / 1000);
        
        // Only save the time that hasn't been auto-saved yet
        const timeToSave = totalElapsed - autoSavedTime;
        
        if (timeToSave > 0) {
          await updateTaskTime(activeTaskId, timeToSave);
          
          // Refresh tasks to update UI with new time spent
          await refreshTasks();
        }
      } catch (error) {
        console.error("Failed to update task time:", error);
      }
    }

    // Stop the current timer
    setIsTimerRunning(false);
    setTimerState("ready");
    setTimerStartTime(null);
    setStartTimeLeft(null);
    setAutoSavedTime(0); // Reset auto-save tracking
    
    // Clear localStorage
    localStorage.removeItem('timerStartTime');
    localStorage.removeItem('currentTimeAtStart');
    localStorage.removeItem('timerRecoveryData'); // Clear recovery data
    localStorage.removeItem('timerBeaconSent'); // Clear beacon data

    if (currentSessionType === "work") {
      
      // Increment session counters (assume work session was completed)
      setCurrentSession(prev => prev + 1);
      setCompletedSessions(prev => prev + 1);
      
      // Check if we've completed all sessions for this preset
      const totalSessions = TIMER_PRESETS[selectedPreset].totalSessions;
      if (completedSessions >= totalSessions - 1) {
        // Preset is complete - reset to beginning (first focus session)
        setTotalCompletedPomodoros(prev => prev + 1);
        
        // Reset for next round
        setCurrentSessionType("work");
        setCurrentSession(1);
        setCompletedSessions(0);
        setTotalTime(TIMER_PRESETS[selectedPreset].workDuration);
        setCurrentTime(0);
        setTimerStartingPosition(0);
        setPausedAtTime(null); // Clear any paused position
        return;
      }
      
      // Go to short break
      const breakDuration = TIMER_PRESETS[selectedPreset].shortBreakDuration;
      setCurrentSessionType("shortBreak");
      setTotalTime(breakDuration);
      setCurrentTime(0);
      setTimerStartingPosition(0);
      setPausedAtTime(null); // Clear any paused position
      
    } else {
      // Skipping break - go to next work session
      setTotalCompletedPomodoros(prev => prev + 1);
      
      // Transition to work
      setCurrentSessionType("work");
      const workDuration = TIMER_PRESETS[selectedPreset].workDuration;
      setTotalTime(workDuration);
      setCurrentTime(0);
      setTimerStartingPosition(0);
      setPausedAtTime(null); // Clear any paused position
    }
  };

  // Get today's incomplete tasks for the player bar
  const todayTasks = useMemo(() => {
    return tasks
      .filter((t) => t.isToday && !t.completed)
      .sort((a, b) => (a.todayOrder ?? 0) - (b.todayOrder ?? 0));
  }, [tasks]);

  const completedTasks = useMemo(() => {
    return tasks
      .filter((task) => task.completed)
      .sort((a, b) => {
        const aTime = timeValue(a.completedAt);
        const bTime = timeValue(b.completedAt);
        return bTime - aTime;
      });
  }, [tasks]);

  // Set active task to first today task if no active task is set
  useEffect(() => {
    if (!activeTaskId && todayTasks.length > 0) {
      setActiveTaskId(todayTasks[0].id);
    }
  }, [todayTasks, activeTaskId]);

  useEffect(() => {
    if (isInitialized && todayTasks.length === 0 && activeTaskId !== null) {
      setActiveTaskId(null);
      (async () => {
        try {
          await settingsService.setSetting("activeTaskId", null);
        } catch (err) {
          console.error("Failed to persist cleared activeTaskId:", err);
        }
      })();
    }
  }, [isInitialized, todayTasks.length, activeTaskId]);

  // Sync activeTaskId when current active task is no longer in todayTasks
  // This mirrors the fallback logic in TaskPlayerBar and ensures highlighting stays in sync
  useEffect(() => {
    if (activeTaskId !== null && todayTasks.length > 0) {
      const activeTaskStillInToday = todayTasks.some(t => t.id === activeTaskId);
      if (!activeTaskStillInToday) {
        const nextActiveId = todayTasks[0].id;
        setActiveTaskId(nextActiveId);
        (async () => {
          try {
            await settingsService.setSetting("activeTaskId", nextActiveId);
          } catch (err) {
            console.error("Failed to persist activeTaskId:", err);
          }
        })();
      }
    }
  }, [todayTasks, activeTaskId]);

  // Periodic auto-save during timer operation
  useEffect(() => {
    let autoSaveIntervalId: NodeJS.Timeout | null = null;

    if (isTimerRunning && activeTaskId && timerStartTime && currentSessionType === "work") {
      // Auto-save every 30 seconds while timer is running
      autoSaveIntervalId = setInterval(async () => {
        try {
          const currentTimerStartTime = timerStartTime; // Capture current value
          const currentActiveTaskId = activeTaskId; // Capture current value
          const currentAutoSavedTime = autoSavedTime; // Capture current value
          
          // Calculate total elapsed time since timer start
          const totalElapsed = Math.floor((Date.now() - currentTimerStartTime) / 1000);
          
          // Calculate only the time that hasn't been saved yet
          const timeToSave = totalElapsed - currentAutoSavedTime;
          
          if (timeToSave > 0) {
            await updateTaskTime(currentActiveTaskId, timeToSave);
            
            // Update the amount of time we've auto-saved (don't reset timer start time!)
            setAutoSavedTime(totalElapsed);
            
            // Refresh tasks to show updated time in UI
            await refreshTasks();
          }
        } catch (error) {
          console.error("Auto-save failed:", error);
        }
      }, 30000); // 30 seconds
    }

    return () => {
      if (autoSaveIntervalId) {
        clearInterval(autoSaveIntervalId);
      }
    };
  }, [isTimerRunning, activeTaskId, timerStartTime, currentSessionType, autoSavedTime, updateTaskTime, refreshTasks]);

  // Core timer logic
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    
    const handleTimerCompleteInternal = async () => {
      if (currentSessionType === "work") {
        // Track time for active task if one is selected
        if (activeTaskId) {
          const workDurationSeconds = TIMER_PRESETS[selectedPreset].workDuration;
          
          // Only save the time that hasn't been auto-saved yet
          const timeToSave = workDurationSeconds - autoSavedTime;
          
          try {
            if (timeToSave > 0) {
              await updateTaskTime(activeTaskId, timeToSave);
            }
            
            // Refresh tasks to update UI with new time spent
            await refreshTasks();
          } catch (error) {
            console.error("Failed to update task time:", error);
          }
        }
        
        // Work session completed - increment session counter
        setCurrentSession(prev => prev + 1);
        setCompletedSessions(prev => prev + 1);
        setAutoSavedTime(0); // Reset auto-save tracking for next session
        
        // Check if we've completed all sessions for this preset
        const totalSessions = TIMER_PRESETS[selectedPreset].totalSessions;
        if (completedSessions >= totalSessions - 1) {
          // Preset is complete
          setTotalCompletedPomodoros(prev => prev + 1);
          
        // Reset for next round or stop
        setCurrentSessionType("work");
        setCurrentSession(1);
        setCompletedSessions(0);
        setTotalTime(TIMER_PRESETS[selectedPreset].workDuration);
        setCurrentTime(0);
        setTimerStartingPosition(0);
        setTimerState("ready");
        setIsTimerRunning(false);
        setAutoSavedTime(0); // Reset auto-save tracking
        setPausedAtTime(null); // Clear any paused position
          return;
        }
        
        // Start short break
        const nextDuration = TIMER_PRESETS[selectedPreset].shortBreakDuration;
        
        // Transition to break
        setCurrentSessionType("shortBreak");
        setTotalTime(nextDuration);
        setCurrentTime(0);
        setTimerStartingPosition(0);
        setPausedAtTime(null); // Clear any paused position
        
        // Auto-start break timer
        setTimeout(() => {
          const startTime = Date.now();
          setTimerStartTime(startTime);
          setStartTimeLeft(nextDuration);
          setTimerStartingPosition(0); // Breaks always start from 0
          setTimerState("running");
          setIsTimerRunning(true);
          localStorage.setItem('timerStartTime', startTime.toString());
          localStorage.setItem('currentTimeAtStart', '0');
        }, 100);
        
      } else {
        // Break completed - start new work session
        setTotalCompletedPomodoros(prev => prev + 1);
        setAutoSavedTime(0); // Reset auto-save tracking for new work session
        
        // Transition to work
        setCurrentSessionType("work");
        const workDuration = TIMER_PRESETS[selectedPreset].workDuration;
        setTotalTime(workDuration);
        setCurrentTime(0);
        setTimerStartingPosition(0);
        setPausedAtTime(null); // Clear any paused position
        
        // Auto-start work timer
        setTimeout(() => {
          const startTime = Date.now();
          setTimerStartTime(startTime);
          setStartTimeLeft(workDuration);
          setTimerStartingPosition(0); // New work sessions always start from 0
          setTimerState("running");
          setIsTimerRunning(true);
          localStorage.setItem('timerStartTime', startTime.toString());
          localStorage.setItem('currentTimeAtStart', '0');
        }, 100);
      }
    };

    if (timerState === "running" && timerStartTime !== null && startTimeLeft !== null) {
      intervalId = setInterval(() => {
        const now = Date.now();
        const elapsedSeconds = Math.floor((now - timerStartTime) / 1000);
        
        // Use React state instead of localStorage to avoid race conditions
        const newCurrentTime = Math.min(totalTime, timerStartingPosition + elapsedSeconds);
        
        // Calculate time remaining
        const timeRemaining = totalTime - newCurrentTime;
        
        // Play countdown beeps in last 5 seconds if enabled
        if (soundSettings.countdownBeeps && timeRemaining > 0 && timeRemaining <= 5) {
          try {
            const audio = new Audio(SOUND_PATHS.countdown);
            audio.play().catch(err => console.error('Failed to play countdown beep:', err));
          } catch (err) {
            console.error('Error creating countdown audio:', err);
          }
        }
        
        // Play 2-minute warning if enabled (exactly at 2:00 remaining)
        if (soundSettings.twoMinWarning && timeRemaining === 120) {
          try {
            const audio = new Audio(SOUND_PATHS.twoMinWarning);
            audio.play().catch(err => console.error('Failed to play 2-min warning:', err));
          } catch (err) {
            console.error('Error creating 2-min warning audio:', err);
          }
        }
        
        setCurrentTime(newCurrentTime);
        
        // Check if timer completed
        if (newCurrentTime >= totalTime) {
          handleTimerCompleteInternal();
          if (intervalId) {
            clearInterval(intervalId);
          }
        }
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [timerState, timerStartTime, startTimeLeft, totalTime, timerStartingPosition, currentSessionType, selectedPreset, completedSessions, activeTaskId, autoSavedTime, updateTaskTime, refreshTasks, soundSettings]);



  return (
    <div className={`${isAppMinimized ? 'relative' : 'flex flex-col'} min-h-screen transition-colors duration-200 ${backgroundImage ? '' : isAppMinimized ? 'bg-gray-50' : 'bg-gray-100'} ${backgroundImage ? '' : 'dark:bg-dark'} ${!isAppMinimized && todayTasks.length > 0 ? 'pb-32' : ''} relative`}>
      {/* Background Layer */}
      {backgroundImage && (
        <div 
          className="fixed inset-0 w-full h-full transition-opacity duration-300"
          style={{
            ...(backgroundImage.startsWith('#') 
              ? { backgroundColor: backgroundImage }
              : { backgroundImage: `url(${backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }
            ),
            zIndex: -10,
          }}
        />
      )}
      {/* Normal Header - Hidden when minimized */}
      {!isAppMinimized && (
        <header className={`fixed top-0 left-0 right-0 transition-all duration-200 pointer-events-none ${
          isYouTubeImmersive || backgroundImage
            ? 'bg-white/80 dark:bg-dark-card/80 backdrop-blur-md shadow-none border-b border-white/20 dark:border-white/10 z-30' 
            : 'bg-white dark:bg-dark-card shadow dark:shadow-none dark:border-b dark:border-dark-border z-30'
        }`}>
        
        {/* Mobile: Two-row layout, Desktop: Single-row layout */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between max-sm:py-4 sm:py-6 px-6">
          
          {/* Row 1 (mobile) / Left side (desktop): Logo and Sign In */}
          <div className="flex items-center justify-between w-full sm:w-auto max-sm:justify-center max-sm:py-4">
            {/* Logo - centered on mobile, left-aligned on desktop */}
            <div className="flex items-center gap-4 pointer-events-auto">
              <img 
                src="/logo.svg" 
                alt="VS" 
                className="max-sm:h-3 sm:h-5"
                style={{
                  filter: darkMode 
                    ? 'none' 
                    : 'brightness(0.69)'
                }}
              />
              <span 
                className="font-semibold"
                style={{ 
                  fontFamily: "'Rethink Sans', sans-serif",
                  letterSpacing: '0.3em',
                  fontSize: '0.8rem',
                  color: darkMode ? '#949494' : '#666666'
                }}
              >
                VIBE SPACE
              </span>
            </div>

            {/* Sign In button - visible on mobile in row 1, hidden on desktop (shows in row 3) */}
            {/* FEATURE FLAG: Hidden until authentication is enabled */}
            {FEATURES.AUTHENTICATION && (
              <div className="sm:hidden pointer-events-auto">
                <UserProfileButton />
              </div>
            )}
          </div>

          {/* Row 2 (mobile) / Center (desktop): Action Buttons */}
          <div className="flex items-center justify-center space-x-2 pointer-events-auto sm:absolute sm:left-1/2 sm:transform sm:-translate-x-1/2">
            <button
              onClick={handleToggleDrawer}
              className={`rounded-full p-2 transition-colors duration-200 focus:outline-none ${
                isDrawerOpen 
                  ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' 
                  : 'text-gray-500 dark:text-dark-secondary hover:bg-gray-100 dark:hover:bg-dark-hover hover:text-gray-900 dark:hover:text-white'
              }`}
              aria-label={isDrawerOpen ? "Close tasks" : "Open tasks"}
            >
              <QueueListIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            <button
              onClick={handleToggleMusicPlayer}
              disabled={showYouTubePlayer}
              className={`rounded-full p-2 transition-colors duration-200 focus:outline-none ${
                showYouTubePlayer 
                  ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 cursor-not-allowed' 
                  : 'text-gray-500 dark:text-dark-secondary hover:bg-gray-100 dark:hover:bg-dark-hover hover:text-gray-900 dark:hover:text-white'
              }`}
              aria-label={showYouTubePlayer ? "YouTube player active" : "Open YouTube player"}
            >
              <MusicalNoteIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            {/* Separator */}
            <div className="h-4 w-px bg-gray-400 dark:bg-gray-500"></div>
            <button
              onClick={toggleDarkMode}
              className="rounded-full p-2 transition-colors duration-200 text-gray-500 dark:text-dark-secondary hover:bg-gray-100 dark:hover:bg-dark-hover hover:text-gray-900 dark:hover:text-white focus:outline-none"
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {darkMode ? (
                <SunIcon className="h-5 w-5" aria-hidden="true" />
              ) : (
                <MoonIcon className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
            <button
              ref={backgroundButtonRef}
              onClick={toggleBackgroundPalette}
              className="rounded-full p-2 transition-colors duration-200 text-gray-500 dark:text-dark-secondary hover:bg-gray-100 dark:hover:bg-dark-hover hover:text-gray-900 dark:hover:text-white focus:outline-none"
              aria-label={backgroundImage ? (backgroundImage.startsWith('#') ? "Change background color" : "Change background image") : "Choose background"}
            >
              <PhotoIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            {/* Minimize button - visible on all screens */}
            <button
              onClick={() => {
                const newState = !isAppMinimized;
                setIsAppMinimized(newState);
                
                // Track app minimized
                posthog?.capture('app_minimized', {
                  minimized: newState
                });
              }}
              className="rounded-full p-2 transition-colors duration-200 text-gray-500 dark:text-dark-secondary hover:bg-gray-100 dark:hover:bg-dark-hover hover:text-gray-900 dark:hover:text-white focus:outline-none"
              aria-label="Toggle minimized mode"
            >
              {isAppMinimized ? (
                <ArrowsPointingOutIcon className="h-5 w-5" aria-hidden="true" />
              ) : (
                <ArrowsPointingInIcon className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          </div>

          {/* Row 3 (desktop only): Sign In button on the right */}
          {/* FEATURE FLAG: Hidden until authentication is enabled */}
          {FEATURES.AUTHENTICATION && (
            <div className="hidden sm:flex items-center pointer-events-auto">
              <UserProfileButton />
            </div>
          )}
        </div>
        </header>
      )}

      {/* Floating Header - Only when minimized */}
      {isAppMinimized && (
        <div className={`fixed z-50 left-0 right-0 flex justify-center ${
          isYouTubeImmersive 
            ? 'top-[5.625rem]' // Positioned to align with YouTube exit button
            : 'top-4'   // Default position when no YouTube immersive
        }`}>
          <div className="flex items-center space-x-2 bg-white/80 dark:bg-dark-card/80 backdrop-blur-lg rounded-full shadow-lg border border-white/20 dark:border-white/10 px-3 py-2">
            <button
              onClick={handleToggleDrawer}
              className="rounded-full p-2 transition-colors duration-200 text-gray-500 dark:text-dark-secondary hover:bg-gray-100 dark:hover:bg-dark-hover hover:text-gray-900 dark:hover:text-white focus:outline-none"
            >
              <QueueListIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            <button
              onClick={handleToggleMusicPlayer}
              disabled={showYouTubePlayer}
              className={`rounded-full p-2 transition-colors duration-200 focus:outline-none ${
                showYouTubePlayer 
                  ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 cursor-not-allowed' 
                  : 'text-gray-500 dark:text-dark-secondary hover:bg-gray-100 dark:hover:bg-dark-hover hover:text-gray-900 dark:hover:text-white'
              }`}
              aria-label={showYouTubePlayer ? "YouTube player active" : "Open YouTube player"}
            >
              <MusicalNoteIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            {/* Separator */}
            <div className="h-4 w-px bg-gray-400 dark:bg-gray-500"></div>
            <button
              onClick={toggleDarkMode}
              className="rounded-full p-2 transition-colors duration-200 text-gray-500 dark:text-dark-secondary hover:bg-gray-100 dark:hover:bg-dark-hover hover:text-gray-900 dark:hover:text-white focus:outline-none"
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {darkMode ? (
                <SunIcon className="h-5 w-5" aria-hidden="true" />
              ) : (
                <MoonIcon className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
            <button
              ref={backgroundButtonRef}
              onClick={toggleBackgroundPalette}
              className="rounded-full p-2 transition-colors duration-200 text-gray-500 dark:text-dark-secondary hover:bg-gray-100 dark:hover:bg-dark-hover hover:text-gray-900 dark:hover:text-white focus:outline-none"
              aria-label={backgroundImage ? (backgroundImage.startsWith('#') ? "Change background color" : "Change background image") : "Choose background"}
            >
              <PhotoIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              onClick={() => {
                const newState = !isAppMinimized;
                setIsAppMinimized(newState);
                
                // Track app minimized
                posthog?.capture('app_minimized', {
                  minimized: newState
                });
              }}
              className="rounded-full p-2 transition-colors duration-200 text-gray-500 dark:text-dark-secondary hover:bg-gray-100 dark:hover:bg-dark-hover hover:text-gray-900 dark:hover:text-white focus:outline-none"
              aria-label="Exit minimized mode"
            >
              <ArrowsPointingOutIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {/* Side Drawer for Task List */}
      <Transition.Root appear show={isDrawerOpen} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 overflow-hidden z-[1100]" onClose={() => setIsDrawerOpen(false)}>
          <div className="absolute inset-0 overflow-hidden">
            {/* backdrop fade */}
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="absolute inset-0 bg-black/30 dark:bg-black/50 transition-opacity" aria-hidden="true" />
            </Transition.Child>

            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
                {/* panel slide */}
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-300"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                    <div className="relative flex h-full flex-col bg-white dark:bg-dark-card shadow-xl p-8">
                      <div className="pb-6 bg-white dark:bg-dark-card flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => setActiveTasksTab("tasks")}
                            className={`px-3 py-1.5 text-sm font-medium rounded-full transition ${
                              activeTasksTab === "tasks"
                                ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                                : "text-gray-500 dark:text-dark-secondary hover:text-gray-900 dark:hover:text-dark-primary"
                            }`}
                          >
                          Tasks
                          </button>
                          <button
                            type="button"
                            onClick={() => setActiveTasksTab("completed")}
                            className={`px-3 py-1.5 text-sm font-medium rounded-full transition ${
                              activeTasksTab === "completed"
                                ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                                : "text-gray-500 dark:text-dark-secondary hover:text-gray-900 dark:hover:text-dark-primary"
                            }`}
                          >
                            <span className="inline-flex items-center">
                              Completed
                            </span>
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsDrawerOpen(false)}
                          className="p-0.5 text-gray-400 hover:text-gray-500 dark:text-dark-secondary dark:hover:text-dark-primary hover:bg-gray-100 dark:hover:bg-dark-hover rounded-lg transition-colors duration-200 focus:outline-none"
                        >
                          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>


                      <div
                        ref={activeTasksTab === "tasks" ? scrollableAreaRef : null}
                        className="relative flex-1 overflow-y-auto"
                      >
                        {activeTasksTab === "tasks" ? (
                        <TaskList
                          tasks={tasks}
                          onAddTask={addTask}
                          onToggleTask={toggleTaskCompletion}
                          onDeleteTask={deleteTask}
                          onUpdateTask={updateTask}
                          sessionType={currentSessionType}
                          onTasksReorder={handleTasksReorder}
                          onSetTaskTodayStatus={handleSetTaskTodayStatus}
                          onRefreshTasks={refreshTasks}
                          activeTaskId={activeTaskId}
                          onSelect={handleTaskSelect}
                          isTimerRunning={isTimerRunning}
                          onActiveTaskMovedToUpcoming={handleActiveTaskMovedToUpcoming}
                        />
                        ) : (
                          <CompletedTasksView
                            tasks={completedTasks}
                            onRestore={handleRestoreTask}
                            onDelete={deleteTask}
                        />
                        )}
                      </div>

                      {activeTasksTab === "tasks" && (
                        <Transition
                          show={showFixedBox}
                          enter="transition-opacity duration-400"
                          enterFrom="opacity-0"
                          enterTo="opacity-100"
                          leave="transition-opacity duration-400"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <div className="pt-4 bg-white dark:bg-dark-card">
                            <form
                              onSubmit={handleFixedBoxSubmit}
                              className="bg-white dark:bg-dark-card rounded-lg border p-3 border-gray-200 dark:border-gray-700
                                         focus-within:border-blue-500 focus-within:dark:border-primary-600"
                            >
                              <textarea
                                ref={fixedBoxInputRef}
                                rows={1}
                                value={fixedBoxTask}
                                onChange={(e) => {
                                  setFixedBoxTask(e.target.value);
                                  autoGrowFixed(e.currentTarget);
                                }}
                                onInput={(e) => autoGrowFixed(e.currentTarget)}
                                placeholder="Add a task here"
                                className="w-full border-0 focus:ring-0 p-0 text-gray-900 dark:text-dark-primary bg-transparent placeholder-gray-500 dark:placeholder-dark-secondary mb-2 whitespace-pre-wrap break-words resize-none overflow-hidden leading-relaxed"
                                onKeyDown={(e) => {
                                  if (e.key === "Escape") {
                                    e.preventDefault();
                                    setFixedBoxTask("");
                                  } else if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleFixedBoxSubmit(e);
                                  }
                                }}
                              />
                              <div className="flex justify-between items-center">
                                <div className="flex items-center space-x-1">
                                  <CategoryModal
                                    selectedCategory={fixedBoxCategory}
                                    onChange={(category: string | undefined) => {
                                      setFixedBoxCategory(category);
                                    }}
                                    onCategoryRenamed={async (oldName: string, newName: string) => {
                                      if (fixedBoxCategory === oldName) {
                                        setFixedBoxCategory(newName);
                                      }
                                      await refreshTasks();
                                    }}
                                  />
                                  <DueDateSelector
                                    selectedDate={fixedBoxDueDate}
                                    onChange={setFixedBoxDueDate}
                                  />
                                </div>
                                <button
                                  type="submit"
                                  disabled={!fixedBoxTask.trim()}
                                  className="px-3 py-1 bg-blue-500 dark:bg-blue-600 text-white text-sm rounded hover:bg-blue-600 dark:hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Add Task
                                </button>
                              </div>
                            </form>
                          </div>
                        </Transition>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* YouTube Player - Single instance, positioned based on mode */}
      {showYouTubePlayer && (
        <div className={`${
          isYouTubeImmersive 
            ? 'fixed inset-0 z-0' 
            : 'relative'
        }`}>
          {isYouTubeImmersive ? (
            <YouTubePlayer 
              isVisible={showYouTubePlayer}
              onClose={() => setShowYouTubePlayer(false)}
              onImmersiveChange={setIsYouTubeImmersive}
              isAppMinimized={isAppMinimized}
              isImmersive={isYouTubeImmersive}
              hasActiveTasks={todayTasks.length > 0}
            />
          ) : (
            <YouTubePlayer 
              isVisible={showYouTubePlayer}
              onClose={() => setShowYouTubePlayer(false)}
              onImmersiveChange={setIsYouTubeImmersive}
              isAppMinimized={isAppMinimized}
              isImmersive={isYouTubeImmersive}
              hasActiveTasks={todayTasks.length > 0}
            />
          )}
        </div>
      )}

      {/* Exit Immersive Mode Button - Rendered outside YouTube container to avoid z-index stacking context issues */}
      {isYouTubeImmersive && showYouTubePlayer && (
        <button
          onClick={() => setIsYouTubeImmersive(false)}
          className="fixed top-[5.75rem] right-6 z-[60] p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-sm"
          title="Exit Full Screen"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      )}

      {/* Main Content - Hidden when minimized or immersive */}
      {!isAppMinimized && !isYouTubeImmersive && (
        <main className={`flex-1 transition-colors duration-200 p-4 md:p-6 pt-20 relative z-10`}>
          <div className="mx-auto max-w-4xl space-y-6 transition-colors duration-200">
            {/* Other main content would go here */}
          </div>
        </main>
      )}



      {/* Background Palette */}
      <BackgroundPalette
        isOpen={showBackgroundPalette}
        onClose={() => setShowBackgroundPalette(false)}
        currentBackground={backgroundImage}
        onBackgroundSelect={handleBackgroundSelect}
        anchorPosition={paletteAnchorPosition}
      />

      {/* Task Player Bar */}
      <div className={shouldMinimizeTaskBar ? `fixed bottom-4 left-1/2 transform -translate-x-1/2 ${isYouTubeImmersive ? 'z-40' : 'z-30'}` : isYouTubeImmersive ? 'relative z-30' : ''}>
        <TaskPlayerBar
          todayTasks={todayTasks}
          currentTaskId={activeTaskId}
          isTimerRunning={isTimerRunning}
          currentTime={currentTime}
          totalTime={totalTime}
          sessionType={currentSessionType}
          selectedPreset={selectedPreset}
          currentSession={currentSession}
          completedSessions={completedSessions}
          totalCompletedPomodoros={totalCompletedPomodoros}
          soundSettings={soundSettings}
          isImmersive={isYouTubeImmersive}
          isMinimized={shouldMinimizeTaskBar}
          onPlayPause={handlePlayPause}
          onSkipBreak={handleSkipBreak}
          onNextTask={handleNextTask}
          onPreviousTask={handlePreviousTask}
          onRestartSession={handleRestartSession}
          onTaskSelect={handleTaskSelect}
          onPresetChange={handlePresetChange}
          onSkipSession={handleSkipSession}
          onSkipTask={handleSkipTask}
          onSoundToggle={toggleSound}
        />
      </div>

      {/* Landing Page */}
      {showLandingPage && (
        <LandingPage 
          onStartPlanning={handleStartPlanning}
          shouldExit={landingPageShouldExit}
          onExitComplete={handleLandingExitComplete}
        />
      )}

      {/* Password Reset Modal */}
      <PasswordResetModal 
        isOpen={showPasswordResetModal}
        onClose={() => setShowPasswordResetModal(false)}
      />

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        type="error"
      />
    </div>
  );
}

// Wrap App with AuthProvider
function AppWithAuth() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}

export default AppWithAuth;
